import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Snackbar, Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Block as RevokeIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { fetchPackages, fetchShares, fetchDownloadEvents, revokeShare } from '../store/slices/deliverySlice';
import { CANONICAL_ORIGIN } from '../config';
import PageHeader from '../components/PageHeader';

function Downloads() {
  const dispatch = useDispatch();
  const { packages, shares, events } = useSelector((state) => state.delivery);
  const [tab, setTab] = React.useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    dispatch(fetchPackages({}));
    dispatch(fetchShares({}));
    dispatch(fetchDownloadEvents({}));
  }, [dispatch]);

  const refresh = () => {
    dispatch(fetchPackages({}));
    dispatch(fetchShares({}));
    dispatch(fetchDownloadEvents({}));
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${CANONICAL_ORIGIN}/d/${token}`);
    setSnackbar({ open: true, message: 'Link copied to clipboard' });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    const gb = bytes / (1024 * 1024 * 1024);
    return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  };

  return (
    <Box>
      <PageHeader
        title="Downloads"
        subtitle="Delivery packages, share links, and download events"
        actions={
          <Tooltip title="Refresh"><IconButton onClick={refresh}><RefreshIcon /></IconButton></Tooltip>
        }
      />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Packages (${packages.length})`} />
        <Tab label={`Share Links (${shares.length})`} />
        <Tab label={`Download Events (${events.length})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Shares</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.filter(p => !p.revoked).map(pkg => (
                <TableRow key={pkg.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={500}>{pkg.project_name}</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{pkg.filename}</Typography></TableCell>
                  <TableCell><Chip label={pkg.source_type} size="small" variant="outlined" /></TableCell>
                  <TableCell>{formatSize(pkg.size_bytes)}</TableCell>
                  <TableCell>
                    {pkg.synced
                      ? <Chip label="Synced" size="small" color="success" variant="outlined" />
                      : <Chip label="Pending" size="small" variant="outlined" />
                    }
                  </TableCell>
                  <TableCell>{pkg.shares?.length || 0}</TableCell>
                </TableRow>
              ))}
              {packages.filter(p => !p.revoked).length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No packages yet. Deliver a Watermark Encode or DCP to create one.</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Downloads</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shares.map(share => {
                const pkg = packages.find(p => p.id === share.package_id);
                const expired = share.expires_at && new Date(share.expires_at) < new Date();
                const revoked = Boolean(share.revoked_at);
                return (
                  <TableRow key={share.id} hover>
                    <TableCell>
                      <Typography variant="body2">{share.recipient_name || '—'}</Typography>
                      {share.recipient_email && <Typography variant="caption" color="text.secondary">{share.recipient_email}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="caption">{pkg?.project_name || `#${share.package_id}`}</Typography></TableCell>
                    <TableCell>{share.download_count}{share.max_downloads ? ` / ${share.max_downloads}` : ''}</TableCell>
                    <TableCell>
                      {share.expires_at ? new Date(share.expires_at).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      {revoked ? <Chip label="Revoked" size="small" color="error" variant="outlined" />
                        : expired ? <Chip label="Expired" size="small" variant="outlined" />
                        : <Chip label="Active" size="small" color="success" variant="outlined" />
                      }
                    </TableCell>
                    <TableCell align="right">
                      {!revoked && !expired && (
                        <>
                          <Tooltip title="Copy link"><IconButton size="small" onClick={() => copyLink(share.token)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Revoke"><IconButton size="small" onClick={() => dispatch(revokeShare(share.id))}><RevokeIcon fontSize="small" color="error" /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {shares.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No share links yet.</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(event => (
                <TableRow key={event.id} hover>
                  <TableCell>{new Date(event.occurred_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={event.status}
                      size="small"
                      color={event.status === 'download' || event.status === 'success' ? 'success' : event.status === 'page_view' ? 'info' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{event.ip_address}</Typography></TableCell>
                  <TableCell>#{event.share_id}</TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No download activity yet.</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Downloads;
